/* @flow */
import {
  type AgeGroup,
  type Customer,
  type Gender,
  type Order,
  type SushiKind,
  ageGroup,
  gender,
  order,
  sushi,
} from './types';
import { Random } from '../../javascripts/utils/Random';
import { ageGroupGen, customerKindGen, genderGen } from './common';

// constants

const firstOrders = {
  [gender.MALE]: [
    { p: 0.1, order: order.forKind(2) },
    { p: 0.9, order: order.forKind(3) },
  ],
  [gender.FEMALE]: [
    { p: 0.1, order: order.forKind(1) },
    { p: 0.9, order: order.forKind(4) },
  ],
};

const secondOrders = {
  [ageGroup.CHILD]: {
    [gender.MALE]: [
      { p: 0.8, order: order.forSushi(sushi.new(2, 1)) },
      { p: 0.2, order: order.forSushi(sushi.new(3, 3)) },
    ],
    [gender.FEMALE]: [
      { p: 0.2, order: order.forSushi(sushi.new(1, 3)) },
      { p: 0.8, order: order.forSushi(sushi.new(2, 3)) },
    ],
  },
  [ageGroup.ADULT]: {
    [gender.MALE]: [
      { p: 0.8, order: order.forSushi(sushi.new(2, 2)) },
      { p: 0.2, order: order.forSushi(sushi.new(4, 3)) },
    ],
    [gender.FEMALE]: [
      { p: 0.8, order: order.forSushi(sushi.new(2, 2)) },
      { p: 0.2, order: order.forSushi(sushi.new(3, 3)) },
    ],
  },
  [ageGroup.MIDDLE_AGE]: {
    [gender.MALE]: [
      { p: 0.2, order: order.forSushi(sushi.new(1, 3)) },
      { p: 0.8, order: order.forSushi(sushi.new(4, 2)) },
    ],
    [gender.FEMALE]: [
      { p: 0.8, order: order.forSushi(sushi.new(3, 1)) },
      { p: 0.2, order: order.forSushi(sushi.new(4, 1)) },
    ],
  },
};

const a = 0.7;
const b = 0.3;

const thirdOrders = {
  [ageGroup.CHILD]: {
    [gender.MALE]: [
      { p: a / 3, order: order.forSushi(sushi.new(1, 1)) },
      { p: a / 3, order: order.forSushi(sushi.new(2, 2)) },
      { p: a / 3, order: order.forSushi(sushi.new(3, 3)) },
      { p: b / 3, order: order.forSushi(sushi.new(1, 2)) },
      { p: b / 3, order: order.forSushi(sushi.new(2, 3)) },
      { p: b / 3, order: order.forSushi(sushi.new(4, 1)) },
    ],
    [gender.FEMALE]: [
      { p: a / 2, order: order.forSushi(sushi.new(1, 1)) },
      { p: a / 2, order: order.forSushi(sushi.new(2, 2)) },
      { p: b / 3, order: order.forSushi(sushi.new(1, 2)) },
      { p: b / 3, order: order.forSushi(sushi.new(2, 3)) },
      { p: b / 3, order: order.forSushi(sushi.new(4, 1)) },
    ],
  },
  [ageGroup.ADULT]: {
    [gender.MALE]: [
      { p: a / 2, order: order.forSushi(sushi.new(2, 3)) },
      { p: a / 2, order: order.forSushi(sushi.new(4, 1)) },
      { p: b / 2, order: order.forSushi(sushi.new(3, 1)) },
      { p: b / 2, order: order.forSushi(sushi.new(4, 2)) },
    ],
    [gender.FEMALE]: [
      { p: a / 3, order: order.forSushi(sushi.new(1, 2)) },
      { p: a / 3, order: order.forSushi(sushi.new(2, 3)) },
      { p: a / 3, order: order.forSushi(sushi.new(4, 1)) },
      { p: b / 3, order: order.forSushi(sushi.new(1, 3)) },
      { p: b / 3, order: order.forSushi(sushi.new(3, 1)) },
      { p: b / 3, order: order.forSushi(sushi.new(4, 2)) },
    ],
  },
  [ageGroup.MIDDLE_AGE]: {
    [gender.MALE]: [
      { p: a / 3, order: order.forSushi(sushi.new(1, 3)) },
      { p: a / 3, order: order.forSushi(sushi.new(3, 1)) },
      { p: a / 3, order: order.forSushi(sushi.new(4, 2)) },
      { p: b / 3, order: order.forSushi(sushi.new(2, 1)) },
      { p: b / 3, order: order.forSushi(sushi.new(3, 2)) },
      { p: b / 3, order: order.forSushi(sushi.new(4, 3)) },
    ],
    [gender.FEMALE]: [
      { p: a / 3, order: order.forSushi(sushi.new(1, 3)) },
      { p: a / 3, order: order.forSushi(sushi.new(3, 1)) },
      { p: a / 3, order: order.forSushi(sushi.new(4, 2)) },
      { p: b / 2, order: order.forSushi(sushi.new(3, 2)) },
      { p: b / 2, order: order.forSushi(sushi.new(4, 3)) },
    ],
  },
};

const forthOrders = {
  [gender.MALE]: [
    { p: 0.8, order: order.forKind(2) },
    { p: 0.2, order: order.forKind(3) },
  ],
  [gender.FEMALE]: [
    { p: 0.8, order: order.forKind(1) },
    { p: 0.2, order: order.forKind(4) },
  ],
};

const fifthOrders: { [AgeGroup]: { [SushiKind]: Order } } = {
  [ageGroup.CHILD]: {
    [4]: order.forSushi(sushi.new(1, 1)),
    [3]: order.forSushi(sushi.new(2, 2)),
    [2]: order.forSushi(sushi.new(3, 3)),
    [1]: order.forSushi(sushi.new(4, 1)),
  },
  [ageGroup.ADULT]: {
    [4]: order.forSushi(sushi.new(1, 2)),
    [3]: order.forSushi(sushi.new(2, 3)),
    [2]: order.forSushi(sushi.new(3, 1)),
    [1]: order.forSushi(sushi.new(4, 1)),
  },
  [ageGroup.MIDDLE_AGE]: {
    [4]: order.forSushi(sushi.new(1, 3)),
    [3]: order.forSushi(sushi.new(2, 1)),
    [2]: order.forSushi(sushi.new(3, 1)),
    [1]: order.forSushi(sushi.new(4, 2)),
  },
};

const makeOrdersGen = (gender: Gender, ageGroup: AgeGroup) => {
  const first = Random.ofProbabilities(...firstOrders[gender].map(({ p, order }) => [order, p]));
  const second = Random.ofProbabilities(...secondOrders[ageGroup][gender].map(({ p, order }) => [order, p]));
  const third = Random.ofProbabilities(...thirdOrders[ageGroup][gender].map(({ p, order }) => [order, p]));
  const forth = Random.ofProbabilities(...forthOrders[gender].map(({ p, order }) => [order, p]));
  const fifth: Random<Order> = Random.const((toMatch, eatens) => {
    if (!eatens || !eatens.length) return false;
    return fifthOrders[ageGroup][eatens[0].kind](toMatch);
  });

  return Random.traverse([first, second, third, forth, fifth], x => x);
};

export const AGE_BASE = {
  [ageGroup.CHILD]: 7,
  [ageGroup.ADULT]: 30,
  [ageGroup.MIDDLE_AGE]: 70,
};

export const AGE_RANGE = {
  [ageGroup.CHILD]: 8,
  [ageGroup.ADULT]: 9,
  [ageGroup.MIDDLE_AGE]: 9,
};

export const customerGen: Random<Customer> = Random.map3(
  genderGen,
  ageGroupGen.map(ag => [ag, AGE_RANGE[ag]]),
  customerKindGen,
  (gender, [ageGrp, range], kind) => {
    let age = range + AGE_BASE[ageGrp];
    return {
      gender,
      ageGroup: ageGrp,
      kind,
      age,
    };
  }
).bind(partial => {
  return makeOrdersGen(partial.gender, partial.ageGroup).map(orders => ({ ...partial, orders }));
});
